"use client"

import { useState } from "react"
import { motion } from "framer-motion"
import { Mail, Phone, MapPin, Clock } from "lucide-react"

const CommonContactUs = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState(null)

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 2000))

      console.log("Form submitted:", formData)
      setSubmitStatus("success")

      // Reset form
      setFormData({
        name: "",
        email: "",
        message: "",
      })
    } catch (error) {
      console.error("Error submitting form:", error)
      setSubmitStatus("error")
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">Get in Touch With Us</h1>
          <p className="text-xl text-slate-400 max-w-4xl mx-auto leading-relaxed">
            Have questions about our talent solutions? Contact us today and our team will get back to you as soon as
            possible.
          </p>
        </motion.div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="bg-slate-800 border border-slate-700 rounded-2xl p-8"
          >
            <h2 className="text-2xl font-bold text-white mb-8">Contact Information</h2>

            {/* Office Hours */}
            <div className="mb-8">
              <div className="flex items-center mb-4">
                <Clock className="w-5 h-5 text-slate-400 mr-3" />
                <h3 className="text-lg font-semibold text-slate-400">Office Hours</h3>
              </div>
              <div className="ml-8 space-y-2">
                <p className="text-white">Monday - Friday: 9:00 AM - 6:00 PM</p>
                <p className="text-white">Saturday: 10:00 AM - 2:00 PM</p>
              </div>
            </div>

            {/* Email */}
            <div className="mb-8">
              <div className="flex items-center mb-4">
                <Mail className="w-5 h-5 text-slate-400 mr-3" />
                <h3 className="text-lg font-semibold text-slate-400">Email</h3>
              </div>
              <div className="ml-8 space-y-2">
                <p className="text-white">contact@techmind.com</p>
                <p className="text-white">support@techmind.com</p>
              </div>
            </div>

            {/* Phone */}
            <div className="mb-8">
              <div className="flex items-center mb-4">
                <Phone className="w-5 h-5 text-slate-400 mr-3" />
                <h3 className="text-lg font-semibold text-slate-400">Phone</h3>
              </div>
              <div className="ml-8">
                <p className="text-white">+1 (555) 123-4567</p>
              </div>
            </div>

            {/* Location */}
            <div>
              <div className="flex items-center mb-4">
                <MapPin className="w-5 h-5 text-slate-400 mr-3" />
                <h3 className="text-lg font-semibold text-slate-400">Location</h3>
              </div>
              <div className="ml-8 space-y-1">
                <p className="text-white">123 Tech Avenue, Suite 100</p>
                <p className="text-white">Silicon Valley, CA 94025</p>
              </div>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="bg-slate-800 border border-slate-700 rounded-2xl p-8"
          >
            <h2 className="text-2xl font-bold text-white mb-8">Send us a Message</h2>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name */}
              <div>
                <label htmlFor="name" className="block text-white text-lg font-medium mb-3">
                  Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Your name"
                  required
                  className="w-full px-4 py-4 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                />
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-white text-lg font-medium mb-3">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Your email address"
                  required
                  className="w-full px-4 py-4 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                />
              </div>

              {/* Message */}
              <div>
                <label htmlFor="message" className="block text-white text-lg font-medium mb-3">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder="Type your message here..."
                  rows={6}
                  required
                  className="w-full px-4 py-4 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-vertical"
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-4 bg-slate-600 hover:bg-slate-500 disabled:bg-slate-700 text-white font-semibold rounded-lg transition-colors duration-200 shadow-lg hover:shadow-xl disabled:cursor-not-allowed"
              >
                {isSubmitting ? "Sending..." : "Send Message"}
              </button>
            </form>

            {/* Success/Error Messages */}
            {submitStatus === "success" && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-6 p-4 bg-green-900 border border-green-700 rounded-lg text-green-200 text-center"
              >
                Thank you for your message! {"We'll"} get back to you soon.
              </motion.div>
            )}

            {submitStatus === "error" && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-6 p-4 bg-red-900 border border-red-700 rounded-lg text-red-200 text-center"
              >
                Sorry, there was an error sending your message. Please try again.
              </motion.div>
            )}
          </motion.div>
        </div>
      </div>
  )
}

export default CommonContactUs
